import { For, Show, createMemo } from "solid-js";
import type { Component } from "solid-js";
import { useChatListQuery } from "../service";
import { useShellStore } from "@ui/state/shell";
import { CustomerCell } from "@modules/bookings/components/CustomerCell";

const formatTimestamp = (date: Date | null): string => {
  if (!date) {
    return "No activity yet";
  }
  const diffMinutes = Math.round((date.getTime() - Date.now()) / 1000 / 60);
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (Math.abs(diffMinutes) < 60) {
    return formatter.format(diffMinutes, "minute");
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) {
    return formatter.format(diffHours, "hour");
  }
  const diffDays = Math.round(diffHours / 24);
  return formatter.format(diffDays, "day");
};

const formatPreview = (preview: string | null): string => {
  if (!preview) {
    return "No messages yet.";
  }
  return preview.length > 160 ? `${preview.slice(0, 157)}...` : preview;
};

const resolve = <T,>(input: T | (() => T)): T => (typeof input === "function" ? (input as () => T)() : input);

export const ChatListView: Component = () => {
  const query = useChatListQuery();
  const {
    state,
    actions: { selectChat },
  } = useShellStore();


  const chats = createMemo(() => resolve(query.data) ?? []);
  const isLoading = () => resolve(query.isLoading);
  const isFetching = () => resolve(query.isFetching);

  const handleSelect = (chatId: string, userId: string | null) => {
    selectChat(chatId, userId);
  };

  return (
    <section class="chat-list-view">
      <header class="chat-list-header">
        <div>
          <h1 class="main-placeholder-title">HappyChat inbox</h1>
          <p>Reach out to trial customers, answer questions, and follow up after cleanings.</p>
        </div>
        <div class="chat-list-actions">
          <button
            type="button"
            class="button-primary refresh-button"
            onClick={() => void query.refetch()}
            disabled={isFetching()}
            title="Refresh"
          >
            <i class={`fa-solid fa-rotate-right ${isFetching() ? "spinning" : ""}`}></i>
          </button>
        </div>
      </header>

      <Show when={!isLoading()} fallback={<div class="placeholder-card">Loading chats...</div>}>
        <Show when={chats().length > 0} fallback={<div class="placeholder-card">No conversations yet.</div>}>
          <table class="chat-table">
            <thead>
              <tr>
                <th>User</th>
                <th class="mobile-hide">Last Message</th>
                <th>Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <For each={chats()}>
                {(chat) => (
                  <tr
                    class={state.selectedChatId === chat.id ? "is-selected" : ""}
                    onClick={() => handleSelect(chat.id, chat.userId)}
                  >
                    <td class="chat-user">
                      <Show when={chat.userId} fallback={<span>{chat.anonSessionId ?? "Unidentified"}</span>}>
                        {(userId) => (
                          <div class="chat-user-info">
                            <CustomerCell userId={userId()} />
                            <Show when={chat.anonSessionId}>
                              {(sessionId) => <span class="chat-user-meta">Session {sessionId()}</span>}
                            </Show>
                          </div>
                        )}
                      </Show>
                    </td>
                    <td class="chat-preview mobile-hide">
                      {formatPreview(chat.lastMessagePreview)}
                    </td>
                    <td class="chat-time">
                      {formatTimestamp(chat.lastMessageAt)}
                    </td>
                    <td>
                      <Show when={chat.unreadForAdmin > 0}>
                        <span class="chat-badge">{chat.unreadForAdmin} new</span>
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </Show>
    </section>
  );
};


